import ToastStack from 'components/desktop/shared/toast-stack';
import { IconClose } from 'components/icons';
import { MediaUnionWithFullUrls } from 'graphql/main/queries';
import { SINGLE_UPLOAD, SingleUploadData } from 'graphql/upload/mutations/single-upload';
import { useFlexMutation, useToast } from 'hooks';
import { compact, filter, gt, isEmpty, isEqual, last, lt, map, size, split } from 'lodash';
import { Fragment, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useFormContext } from 'react-hook-form';
import { documentType } from '../../attachments';

interface Media {
  id: string;
  fileId?: string;
  name?: string;
  originalUrl?: string;
  type?: string;
  size?: number;
  statusUpload?: boolean;
}
interface Props {
  field?: string;
  maxFiles?: number;
  documents?: MediaUnionWithFullUrls[];
}

const DEFAULT_MAX_FILES = 10;

const GraphQLUploadDocument = ({ field, maxFiles = DEFAULT_MAX_FILES, documents }: Props) => {
  const { toasts, addToast } = useToast();
  const [upload] = useFlexMutation<SingleUploadData>('upload', SINGLE_UPLOAD);
  const mediaForm = useFormContext();
  const { setValue } = mediaForm;
  const [uploadedMedia, setUploadedMedia] = useState<Media[]>([]);
  const generateId = () => {
    return 'id' + new Date().getTime() + Math.random();
  };

  const { getRootProps, getInputProps } = useDropzone({
    multiple: true,
    maxSize: 1024 * 1024 * 10,
    accept: {
      'image/*': [],
      'application/vnd.ms-excel': ['.xls', '.slk', '.xla', '.xlt', '.xlw'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/pdf': ['.pdf'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },

    onDropRejected: () => {
      addToast('error', 'Kích thước hoặc số lượng tập tin vượt quá giới hạn cho phép');
    },
    onDrop: async (acceptedFiles) => {
      if (gt(size(acceptedFiles) + size(uploadedMedia), maxFiles)) {
        addToast('error', 'Kích thước hoặc số lượng tập tin vượt quá giới hạn cho phép');
      } else {
        const acceptedFileWithIds = map(acceptedFiles, (file) => ({
          id: generateId(),
          file: file,
        }));
        setUploadedMedia((previousMedia) => [
          ...previousMedia,
          ...map(acceptedFileWithIds, ({ id, file }) => {
            return {
              id,
              fileId: undefined,
              url: URL.createObjectURL(file),
              type: file?.type,
              name: file?.name,
              size: file?.size,
              statusUpload: true,
            };
          }),
        ]);
        for (let index = 0; lt(index, size(acceptedFileWithIds)); index++) {
          const document = acceptedFileWithIds[index];
          const { errors: uploadErrors, data: uploadData } = await upload({
            variables: { file: document.file },
            onError: (error) => {
              setUploadedMedia((previousMedia) =>
                filter(previousMedia, (file) => !isEqual(file.id, document.id)),
              );
              addToast('error', error.message);
            },
          });
          if (uploadErrors) {
            setUploadedMedia((previousMedia) =>
              filter(previousMedia, (file) => !isEqual(file.id, document.id)),
            );
            addToast('error', uploadErrors?.[0]?.message);
          } else {
            const { singleUpload } = uploadData ?? {};
            const { payload } = singleUpload ?? {};
            const { id, fileId: documentId } = payload ?? {};
            setUploadedMedia((previousMedia) =>
              map(previousMedia as any, (media) =>
                isEqual(media.id, document.id)
                  ? {
                      ...media,
                      id,
                      url: URL.createObjectURL(document?.file),
                      fileId: documentId,
                      statusUpload: false,
                    }
                  : media,
              ),
            );
          }
        }
      }
    },
    onError: (error) => {
      addToast('error', error.message);
    },
  });

  const viewDocument = (url?: string) => {
    window.open(url);
  };
  const removeMediaFile = (id: string) => {
    setUploadedMedia(filter(uploadedMedia, (file) => !isEqual(file.id, id)));
  };

  useEffect(() => {
    setValue(`${field}`, compact(map(uploadedMedia, (media) => media.fileId)));
  }, [uploadedMedia]);
  useEffect(() => {
    documents &&
      setUploadedMedia(
        map(documents, (document) => ({ ...document, id: generateId(), statusUpload: false })),
      );
  }, [documents]);

  return (
    <Fragment>
      <div className='space-y-[24px]'>
        <h2 className='text-[18px] font-[600] leading-[23px]'>Tài liệu đính kèm</h2>
        <div className='h-[1px] w-full bg-stroke' />
        <div className='flex flex-col items-center'>
          <div className='flex w-full flex-col'>
            <span className='text-center text-text2'>
              Bấm vào nút &quot;Chọn tệp tải lên&quot; để đăng tải tài liệu
            </span>
            <span className='text-center text-text2'>
              Lưu ý: Mỗi tập tin không quá 10MB, tối đa {maxFiles} tệp
            </span>
          </div>
          <button
            type='button'
            className='!mt-[16px] flex h-[32px] items-center justify-center rounded-[999px] border-[1px] border-dashed border-primary px-[8px]'
            {...getRootProps()}
          >
            <IconClose className='h-[24px] w-[24px] rotate-[45deg] text-primary' />
            <span className='w-[136px] text-center text-[13px] font-[600] text-primary'>
              Chọn tệp tải lên
            </span>
            <input {...getInputProps()} />
          </button>
        </div>
        {!isEmpty(uploadedMedia) && (
          <div className='!mt-[-2px] flex w-full flex-col grid'>
            {map(uploadedMedia, (document) => {
              return (
                <div
                  key={document.id}
                  className='mt-[24px] flex w-full items-center space-x-[16px] '
                >
                  {document?.statusUpload ? (
                    <Fragment>
                      <div
                        className={`h-[40px] max-h-[40px] w-[40px] min-w-[40px] bg-[url("/icons/document-types/file.svg")] bg-cover bg-center bg-no-repeat `}
                      />
                      <div className='flex w-full flex-col truncate'>
                        <div className=' flex  w-full items-center space-x-[16px] '>
                          <span className='w-full truncate text-left font-semibold leading-[18px]'>
                            {document?.name}
                          </span>
                          <span className='font-semi text-[12px] leading-[15px] text-text2'>
                            {document?.size && `${(document?.size / (1024 * 1024)).toFixed(3)} Mb`}
                          </span>
                        </div>
                        <div className='relative mt-[8px] h-[12px] w-full overflow-hidden rounded-[4px] bg-secondary-light'>
                          <div className='h-full w-full -translate-x-full transform animate-indeterminate bg-primary text-white' />
                          <div className='absolute top-[-3px] left-[40%] text-[10px] text-white '>
                            Đang tải lên...
                          </div>
                        </div>
                      </div>
                      <div className='flex h-[30px] w-[30px] min-w-[30px] items-center justify-center rounded-[4px] bg-gray-200'>
                        <button type='button' onClick={() => removeMediaFile(document.id)}>
                          <IconClose className='h-[18px] w-[18px] text-red-500' />
                        </button>
                      </div>
                    </Fragment>
                  ) : (
                    <Fragment>
                      {(documentType as any)[last(split(document?.name, '.')) as string] || (
                        <div
                          className={`h-[40px] max-h-[40px] w-[40px] min-w-[40px] bg-[url("/icons/document-types/file.svg")] bg-cover bg-center bg-no-repeat `}
                        />
                      )}
                      <div className='wrap ml-[16px] flex w-full flex-col space-y-[6px] truncate'>
                        <button
                          type='button'
                          className='w-full truncate text-left font-semibold leading-[18px] md:w-auto'
                          onClick={() => {
                            viewDocument(document?.originalUrl);
                          }}
                        >
                          {document?.name}
                        </button>
                        <span className='font-semi text-[12px] leading-[15px] text-text2'>
                          {document?.size && `${(document?.size / (1024 * 1024)).toFixed(3)} Mb`}
                        </span>
                      </div>
                      <div className='flex h-[30px] w-[30px] min-w-[30px] items-center justify-center rounded-[4px] bg-gray-200'>
                        <button type='button' onClick={() => removeMediaFile(document.id)}>
                          <IconClose className='h-[18px] w-[18px] text-red-500' />
                        </button>
                      </div>
                    </Fragment>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <ToastStack toasts={toasts} />
    </Fragment>
  );
};

export default GraphQLUploadDocument;
